import type { QueryClient } from '@tanstack/react-query';
import type { DeviceConfigurationApi } from '@model/DeviceConfiguration';
import { getConfiguration } from '@services/configurationManager';
import { fetchSessionConfiguration } from '../packages/spectral-workspace/queries/prefetchSessionConfigurations';
import { getVerificationHrDataSource } from '../ai-verification/resolveVerificationSnapshot';
import type { ValidationInputSession } from '../types';
import { runSpectralAnalysis } from '../utils/spectralAnalysis';

export type EffectiveSessionDataSource = 'hrData_repaired' | 'hrData';

export interface ResolvedEffectiveSessionValidation {
  sessionId: number;
  dataSource: EffectiveSessionDataSource;
  validation?: ReturnType<typeof runSpectralAnalysis>['validations'][number];
  excluded?: ReturnType<typeof runSpectralAnalysis>['excludedSessions'][number];
}

export const resolveEffectiveSessionValidationFromConfiguration = (
  sessionId: number,
  configuration: DeviceConfigurationApi
): ResolvedEffectiveSessionValidation => {
  const session = configuration as ValidationInputSession;
  const dataSource = getVerificationHrDataSource(session);
  const pipeline = runSpectralAnalysis([session]);

  return {
    sessionId,
    dataSource,
    validation: pipeline.validations[0],
    excluded: pipeline.excludedSessions[0],
  };
};

export const resolveEffectiveSessionValidation = async (
  sessionId: number
): Promise<ResolvedEffectiveSessionValidation> => {
  const configuration = await getConfiguration(sessionId);
  return resolveEffectiveSessionValidationFromConfiguration(
    sessionId,
    configuration.data
  );
};

export const resolveEffectiveSessionValidations = async (
  sessionIds: number[]
): Promise<ResolvedEffectiveSessionValidation[]> => {
  const uniqueIds = [...new Set(sessionIds)];

  return Promise.all(
    uniqueIds.map(sessionId => resolveEffectiveSessionValidation(sessionId))
  );
};

export const fetchConfigurationForSession = async (
  queryClient: QueryClient,
  sessionId: number
): Promise<DeviceConfigurationApi> =>
  fetchSessionConfiguration(queryClient, sessionId);

export const loadEffectiveSessionHydration = async (
  queryClient: QueryClient,
  sessionId: number
) => {
  const configuration = await fetchConfigurationForSession(
    queryClient,
    sessionId
  );

  return {
    sessionId,
    configuration,
    resolved: resolveEffectiveSessionValidationFromConfiguration(
      sessionId,
      configuration
    ),
  };
};
